import React, { useContext, useEffect, useState } from 'react';
import { StyledCutLegend } from './FormComponents/Form.styles';
import Column from "components/Grid/Column";
import Row from "components/Grid/Row";
import { useDavidApi } from 'utility/DavidApi/DavidApi';
import PlaceAutocomplete from "components/PlaceAutocomplete/PlaceAutocomplete";
import CutMapView from "../Layout/MapView/CutMapView";
import { convertAddressComponentsToNamedProperties } from 'utility/googleMaps';
import FormField from './FormComponents/FormField';
import SelectField from './FormComponents/SelectField';
import TextareaField from './FormComponents/TextareaField';
import CheckboxField from './FormComponents/CheckboxField';
import MeasurementForm from "./MeasurementForm";
import TextField from "./FormComponents/TextField";
import CutStepForm from "./CutStepForm";
import { FormContext } from "./FormComponents/Form";
import { ArrayFieldEntryContext } from "./FormComponents/ArrayFieldEntry";
import ArrayField from "./FormComponents/ArrayField";
import SpecialForm from "./SpecialForm";
import { resolveCutSurface, SURFACE_OPTIONS } from 'utility/productSurfaces';

// Subclients are only relevant for this client; the field is hidden for everyone else.
const EVANSVILLE_WATER_SEWER_CLIENT_NAME = 'Evansville Water & Sewer Utility';

// The dropdown is fixed to the shared surface list. The selected surface string is
// stored as the cut's product and drives which surface-type fields are shown.
const PRODUCT_CHOICES = SURFACE_OPTIONS.map(surface => ({
    label: surface,
    value: { id: surface },
}));

const GOOGLE_MAPS_API_KEY = 'AIzaSyBsm2iKNeKYNsoCOxXVy0oF0qOoZCuW6lQ';

// Bias address search toward the Evansville, IN area so local results rank first
// while still surfacing places elsewhere (a soft preference, not a hard cutoff).
// Module-level so these are stable references and don't re-create the autocomplete
// element each render.
const EVANSVILLE_BIAS = {
    center: { lat: 37.9716, lng: -87.5711 },
    radius: 40000, // meters (~25 miles, covers the metro + tri-state core)
};
const INCLUDED_REGION_CODES = ['us'];

function CutUpdatedForm({cutInitialState}) {
    const api = useDavidApi();
    const formContext = useContext(FormContext);
    const cutFieldEntryContext = useContext(ArrayFieldEntryContext);
    const [cut, setCut] = useState(cutInitialState);
    const [subclientChoices, setSubclientChoices] = useState([]);
    const [addressSelected, setAddressSelected] = useState({});
    const [convertedPlaceProperties, setConvertedPlaceProperties] = useState({});

    let coordinates = [cut.latitude, cut.longitude];

    // Resolve the project's selected client name (lookup covers newly-picked clients
    // that only carry an id; the embedded name covers the edit/loaded case).
    const projectClientId = formContext?.values?.client?.id;
    const projectClientName = api.clientsById?.[projectClientId]?.name ?? formContext?.values?.client?.name;
    const showSubclient = projectClientName?.trim().toLowerCase() === EVANSVILLE_WATER_SEWER_CLIENT_NAME.toLowerCase();

    useEffect(() => {
        if (cutFieldEntryContext.values !== undefined) {
            setCut(cutFieldEntryContext.values);
        }
    }, [cutFieldEntryContext.values]);

    useEffect(() => {
        if (addressSelected && Object.keys(addressSelected).length !== 0) {
            setConvertedPlaceProperties( convertAddressComponentsToNamedProperties(addressSelected) );
            setGeolocation(addressSelected?.geometry?.location?.lat(), addressSelected?.geometry?.location?.lng());
        }
    }, [addressSelected]);

    useEffect(() => {
        // Only sync the location fields once the user has actually picked an address
        // (which populates convertedPlaceProperties). Running on mount with an empty
        // object would clobber values seeded from the first cut when adding a new cut.
        if (!convertedPlaceProperties || Object.keys(convertedPlaceProperties).length === 0) {
            return;
        }

        if (cutFieldEntryContext.name !== null) {
            formContext.inputChangeHandler(cutFieldEntryContext.name + '.address', convertedPlaceProperties?.street_number?.long_name || '');
            formContext.inputChangeHandler(cutFieldEntryContext.name + '.street', convertedPlaceProperties?.route?.long_name || '');
            formContext.inputChangeHandler(cutFieldEntryContext.name + '.city', convertedPlaceProperties?.locality?.long_name || '');
            formContext.inputChangeHandler(cutFieldEntryContext.name + '.state', convertedPlaceProperties?.administrative_area_level_1?.short_name || '');
            formContext.inputChangeHandler(cutFieldEntryContext.name + '.zipcode', convertedPlaceProperties?.postal_code?.long_name || '');
        }
    }, [convertedPlaceProperties]);

    const setGeolocation = (lat, lng) => {
        formContext.inputChangeHandler(cutFieldEntryContext.name + '.latitude', lat);
        formContext.inputChangeHandler(cutFieldEntryContext.name + '.longitude', lng);
        coordinates = [lat, lng];
    }

    const currentDate = new Date();
    currentDate.setHours(0,0,0,0);

    // Surface drives both the dropdown value and which surface-type fields show.
    const selectedSurface = resolveCutSurface(cut);

    useEffect(() => {
        if (!showSubclient) {
            return;
        }

        (async () => {
            const response = await api.getSubclients();

            if (!response?.length) {
                setSubclientChoices([]);
                return;
            }

            setSubclientChoices([
                { label: 'None', value: '' },
                ...response.map(subclient => ({
                    label: subclient.name,
                    value: { id: subclient.id },
                })),
            ]);
        })();
    }, [api.subclients, showSubclient]);

    return (
        <Row gutter={15}>
            <Column width={1/2}>
                <StyledCutLegend>
                    <h4 style={{marginBottom:'15px'}}>Product:</h4>
                    <SelectField
                        label=""
                        name="product"
                        placeholder="None Selected"
                        placeholderSelectable
                        required
                        value={selectedSurface ? { id: selectedSurface } : ''}
                        choices={PRODUCT_CHOICES}
                        afterChangeEvent={ (e) => {
                            const product = JSON.parse(e.target.value)
                            formContext.inputChangeHandler(cutFieldEntryContext.name + '.product', product)
                        }}
                    />

                    {selectedSurface === "Special" &&
                        <ArrayField
                            name="specials"
                            label="Add Additional Work"
                            entryLabelSingular="Additional Work"
                            entryComponent={<SpecialForm />}
                        />
                    }

                    {showSubclient && subclientChoices.length > 0 && (
                        <>
                            <h4 style={{marginTop:"20px", marginBottom:'15px'}}>Subclient:</h4>
                            <SelectField
                                label=""
                                name="subClient"
                                placeholder="Select One..."
                                value={cut?.subClient ? { id: cut.subClient.id } : ''}
                                choices={subclientChoices}
                                afterChangeEvent={(e) => {
                                    let subClient = null;
                                    try {
                                        subClient = e.target.value ? JSON.parse(e.target.value) : null;
                                    } catch (err) {
                                        subClient = null;
                                    }
                                    formContext.inputChangeHandler(cutFieldEntryContext.name + '.subClient', subClient);
                                }}
                            />
                        </>
                    )}

                    {(selectedSurface === "Asphalt" || selectedSurface === "Concrete") && (
                        <>
                            <h4 style={{marginTop:"20px", marginBottom:'15px'}}>Surface Type:</h4>

                            <CheckboxField
                                label="Driveway"
                                name={cutFieldEntryContext.name + '.driveway'}
                            />

                            {selectedSurface === "Concrete" && (
                                <>
                                    <CheckboxField
                                        label="Curb"
                                        name={cutFieldEntryContext.name + '.curb'}
                                    />
                                    <CheckboxField
                                        label="Mono Curb"
                                        name={cutFieldEntryContext.name + '.monoCurb'}
                                    />
                                    <CheckboxField
                                        label="Sidewalk"
                                        name={cutFieldEntryContext.name + '.sidewalk'}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {/* {[1].includes(product) &&
                        <MeasurementForm fieldName={cutFieldEntryContext.name} cut={cut} product={1} />
                    } */}

                    {(selectedSurface && selectedSurface !== "Special") &&
                        <MeasurementForm fieldName={cutFieldEntryContext.name} cut={cut} product={selectedSurface} />
                    }

                    {(selectedSurface && selectedSurface !== "Special") &&
                        <div>
                            <h4 style={{marginTop:"10px", marginBottom:'15px'}}>
                                Current Step:
                            </h4>
                            <CutStepForm fieldName={cutFieldEntryContext.name} cut={cutFieldEntryContext.values} />
                            <TextareaField
                                label="If applicable, please list any items to be picked up:"
                                name="collectComments"
                                style={{marginTop: '15px'}}
                            />
                        </div>
                    }

                    <h4 style={{marginTop:"20px", marginBottom:'15px'}}>Location:</h4>

                    <FormField label="Search Address">
                        <PlaceAutocomplete
                            className="auto-complete-input"
                            apiKey={GOOGLE_MAPS_API_KEY}
                            onPlaceSelected={(place) => { setAddressSelected(place); }}
                            includedRegionCodes={INCLUDED_REGION_CODES}
                            locationBias={EVANSVILLE_BIAS}
                            placeholder="Type here to search..."
                        />
                    </FormField>
                    <TextField
                        label="Street Number"
                        name="address"
                    />
                    <TextField
                        label="Street"
                        name="street"
                    />
                    <Row gutter={15}>
                        <Column width={1/3}>
                            <TextField
                                label="City"
                                name="city"
                            />
                        </Column>
                        <Column width={1/3}>
                            <TextField
                                label="State"
                                name="state"
                            />
                        </Column>
                        <Column width={1/3}>
                            <TextField
                                label="Zip Code"
                                name="zipcode"
                            />
                        </Column>
                    </Row>

                    <TextareaField
                        label="Comments"
                        name="comments"
                    />
                </StyledCutLegend>

            </Column>
            <Column width={1/2}>
                <CutMapView
                    coordinates={coordinates}
                    cutMarkerDragendHandler={({lat, lng}) => setGeolocation(lat, lng)}
                    id={cut.id}
                    address={cut.address}
                    street={cut.street}
                    state={cut.state}
                    zipcode={cut.zipcode}
                />
            </Column>
        </Row>
    )
  }

export default CutUpdatedForm;
